// ── OwnerBadge.jsx ───────────────────────────────────────────────────────────
// علامة تمييز مالكي الموقع — تاج SVG احترافي نظيف + tooltip ذهبي أنيق

import React, { useState, useRef, useEffect } from "react";
import "./OwnerBadge.css";

/**
 * OwnerBadge — شارة مالك الموقع الحصرية
 *
 * Props:
 *   size     — حجم الأيقونة بالـ px (افتراضي: 16)
 *   showTip  — هل تظهر tooltip عند التحويم؟ (افتراضي: true)
 *   isAr     — هل الواجهة عربية؟ (افتراضي: false)
 *   className — CSS class إضافي
 */
const OwnerBadge = ({ size = 16, showTip = true, isAr = false, className = "" }) => {
  const [tipVisible, setTipVisible] = useState(false);
  const timerRef = useRef(null);

  const label = isAr ? "مالك الموقع" : "Site Owner";
  const tip   = isAr
    ? "هذا الحساب خاص بأحد مالكي Leaks Mercy"
    : "This account belongs to a Leaks Mercy owner";

  const handleEnter = () => {
    if (!showTip) return;
    timerRef.current = setTimeout(() => setTipVisible(true), 120);
  };
  const handleLeave = () => {
    clearTimeout(timerRef.current);
    setTipVisible(false);
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return (
    <span
      className={`ob-root ${className}`}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={handleLeave}
      aria-label={label}
      title={!showTip ? label : undefined}
      role="img"
    >
      {/* ── أيقونة التاج الاحترافية ── */}
      <svg
        className="ob-icon"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <defs>
          {/* تدرج جسم التاج */}
          <linearGradient id="ob-body-g" x1="12" y1="2" x2="12" y2="22" gradientUnits="userSpaceOnUse">
            <stop offset="0%"   stopColor="#FFE566" />
            <stop offset="42%"  stopColor="#FFD000" />
            <stop offset="75%"  stopColor="#E09000" />
            <stop offset="100%" stopColor="#B07000" />
          </linearGradient>

          {/* تدرج القاعدة السفلية */}
          <linearGradient id="ob-base-g" x1="12" y1="14" x2="12" y2="22" gradientUnits="userSpaceOnUse">
            <stop offset="0%"   stopColor="#F0B000" />
            <stop offset="100%" stopColor="#7A5000" />
          </linearGradient>

          {/* ضوء مركزي على القاعدة */}
          <linearGradient id="ob-shine-g" x1="4" y1="14" x2="20" y2="17" gradientUnits="userSpaceOnUse">
            <stop offset="0%"   stopColor="rgba(255,255,200,0)" />
            <stop offset="50%"  stopColor="rgba(255,255,200,0.6)" />
            <stop offset="100%" stopColor="rgba(255,255,200,0)" />
          </linearGradient>

          {/* جوهرة مركزية حمراء */}
          <radialGradient id="ob-gem-r" cx="40%" cy="33%" r="58%">
            <stop offset="0%"   stopColor="#FF809A" />
            <stop offset="45%"  stopColor="#D8002E" />
            <stop offset="100%" stopColor="#7A0018" />
          </radialGradient>

          {/* جواهر زرقاء */}
          <radialGradient id="ob-gem-b" cx="38%" cy="30%" r="62%">
            <stop offset="0%"   stopColor="#90CCFF" />
            <stop offset="50%"  stopColor="#0066DD" />
            <stop offset="100%" stopColor="#002888" />
          </radialGradient>

          {/* توهج التاج */}
          <filter id="ob-glow" x="-20%" y="-25%" width="140%" height="160%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="0.7" result="b" />
            <feFlood floodColor="#FFB800" floodOpacity="0.75" result="c" />
            <feComposite in="c" in2="b" operator="in" result="g" />
            <feMerge>
              <feMergeNode in="g" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/*
          جسم التاج الكامل — مسار واحد نظيف
          ثلاثة أسنان: يسار + وسط (أعلى) + يمين
        */}
        <path
          d="
            M2.5 21
            L2.5 13.5
            L5.5 16.5
            L9.2 6.5
            L12 10
            L14.8 6.5
            L18.5 16.5
            L21.5 13.5
            L21.5 21
            Q21.5 22 20.5 22
            L3.5 22
            Q2.5 22 2.5 21
            Z
          "
          fill="url(#ob-body-g)"
          filter="url(#ob-glow)"
        />

        {/* ضوء أفقي على حافة القاعدة */}
        <rect x="2.5" y="13.5" width="19" height="3" fill="url(#ob-shine-g)" />

        {/* الجزء السفلي أغمق */}
        <path
          d="M2.5 17.5 L2.5 21 Q2.5 22 3.5 22 L20.5 22 Q21.5 22 21.5 21 L21.5 17.5 Z"
          fill="url(#ob-base-g)"
          opacity="0.65"
        />

        {/* خط فاصل ناعم */}
        <line x1="2.5" y1="14" x2="21.5" y2="14"
          stroke="rgba(255,240,120,0.5)" strokeWidth="0.7" />
        <line x1="2.5" y1="18.5" x2="21.5" y2="18.5"
          stroke="rgba(120,70,0,0.25)" strokeWidth="0.6" />

        {/* ── جواهر رؤوس الأسنان ── */}

        {/* جوهرة السن الأيسر */}
        <circle cx="5.5"  cy="16.5" r="1.55" fill="url(#ob-gem-b)" />
        <circle cx="5.1"  cy="15.9" r="0.55" fill="rgba(255,255,255,0.65)" />

        {/* الجوهرة المركزية — أكبر */}
        <circle cx="12"   cy="10"   r="2.05" fill="url(#ob-gem-r)" />
        <circle cx="11.4" cy="9.1"  r="0.75" fill="rgba(255,255,255,0.7)" />
        <circle cx="11.0" cy="8.8"  r="0.32" fill="rgba(255,255,255,0.9)" />

        {/* جوهرة السن الأيمن */}
        <circle cx="18.5" cy="16.5" r="1.55" fill="url(#ob-gem-b)" />
        <circle cx="18.1" cy="15.9" r="0.55" fill="rgba(255,255,255,0.65)" />

        {/* نقاط الأسنان المتوسطة (يسار/يمين) */}
        <circle cx="9.2"  cy="6.5"  r="1.0" fill="#FFE566" opacity="0.8" />
        <circle cx="14.8" cy="6.5"  r="1.0" fill="#FFE566" opacity="0.8" />

        {/* بريق القمة */}
        <circle cx="12" cy="7.2" r="0.42" fill="rgba(255,255,255,0.65)" />
      </svg>

      {/* ── Tooltip ── */}
      {showTip && tipVisible && (
        <span className={`ob-tooltip ${isAr ? "ob-tooltip--rtl" : ""}`} role="tooltip">
          <span className="ob-tooltip-inner">
            {/* أيقونة تاج مصغر داخل الـ tooltip */}
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
              <defs>
                <linearGradient id="ob-tip-g" x1="12" y1="2" x2="12" y2="22" gradientUnits="userSpaceOnUse">
                  <stop offset="0%"  stopColor="#FFE566" />
                  <stop offset="55%" stopColor="#FFD000" />
                  <stop offset="100%" stopColor="#B07000" />
                </linearGradient>
              </defs>
              <path
                d="M2.5 21 L2.5 13.5 L5.5 16.5 L9.2 6.5 L12 10 L14.8 6.5 L18.5 16.5 L21.5 13.5 L21.5 21 Q21.5 22 20.5 22 L3.5 22 Q2.5 22 2.5 21 Z"
                fill="url(#ob-tip-g)"
              />
              <circle cx="12"   cy="10"   r="1.8" fill="#D8002E" />
              <circle cx="5.5"  cy="16.5" r="1.3" fill="#0066DD" />
              <circle cx="18.5" cy="16.5" r="1.3" fill="#0066DD" />
            </svg>
            {tip}
          </span>
          <span className="ob-tooltip-arrow" />
        </span>
      )}
    </span>
  );
};

export default OwnerBadge;
